/**
 * accommodationAlgorithm.ts
 * Smart room distribution algorithm for accommodation booking
 *
 * Distributes participants across room types using weighted percentages
 * to create a realistic, balanced room allocation.
 */

export interface RoomType {
  id: number;
  bedsPerUnit: number;
  price: number;
  grossPrice: number;
  name: string;
}

// Target percentages for each room size (of total beds)
const TARGET_PERCENTAGES: Record<number, number> = {
  8: 0.15,  // FeWo: max 15%
  4: 0.25,  // Vierbett: max 25%
  3: 0.20,  // Dreibett: max 20%
  2: 0.35,  // Doppel: max 35%
  1: 0.10   // Einzel: max 10%
};

/**
 * Calculate optimal room distribution for given participant count.
 *
 * Algorithm in 3 phases:
 * 1. Percentage-based allocation (realistic distribution)
 * 2. Fill remainder with largest rooms
 * 3. Cover final rest with smallest room
 *
 * @param participantCount Number of participants to accommodate
 * @param roomTypes Available room types with beds per unit
 * @returns Map of roomId -> quantity
 */
export function calculateOptimalDistribution(
  participantCount: number,
  roomTypes: RoomType[]
): Map<number, number> {
  const result = new Map<number, number>();

  if (participantCount <= 0 || roomTypes.length === 0) {
    return result;
  }

  // Sort by beds descending (largest first)
  const sorted = [...roomTypes].sort((a, b) =>
    (b.bedsPerUnit || 1) - (a.bedsPerUnit || 1)
  );

  let remainingBeds = participantCount;

  // PHASE 1: Percentage-based allocation
  for (const room of sorted) {
    const beds = room.bedsPerUnit || 1;
    const maxPercent = TARGET_PERCENTAGES[beds] || 0.20;
    const maxBedsForType = Math.floor(participantCount * maxPercent);
    const roomsOfType = Math.min(
      Math.floor(maxBedsForType / beds),
      Math.floor(remainingBeds / beds)
    );

    if (roomsOfType > 0) {
      result.set(room.id, roomsOfType);
      remainingBeds -= roomsOfType * beds;
    }
  }

  // PHASE 2: Fill remainder with largest rooms that fit
  for (const room of sorted) {
    if (remainingBeds <= 0) break;
    const beds = room.bedsPerUnit || 1;
    const additionalRooms = Math.floor(remainingBeds / beds);

    if (additionalRooms > 0) {
      const current = result.get(room.id) || 0;
      result.set(room.id, current + additionalRooms);
      remainingBeds -= additionalRooms * beds;
    }
  }

  // PHASE 3: Cover final rest with smallest room
  if (remainingBeds > 0 && sorted.length > 0) {
    const smallest = sorted[sorted.length - 1];
    const beds = smallest.bedsPerUnit || 1;
    const needed = Math.ceil(remainingBeds / beds);
    const current = result.get(smallest.id) || 0;
    result.set(smallest.id, current + needed);
  }

  return result;
}

/**
 * Calculate total beds from selected rooms
 */
export function calculateTotalBeds(
  selectedRooms: Map<number, number>,
  roomTypes: RoomType[]
): number {
  let total = 0;
  selectedRooms.forEach((qty, roomId) => {
    const room = roomTypes.find(r => r.id === roomId);
    total += qty * (room?.bedsPerUnit || 1);
  });
  return total;
}

/**
 * Calculate total price from selected rooms
 * Price = quantity * bedsPerUnit * pricePerPerson (for PerPerson pricing)
 */
export function calculateTotalPrice(
  selectedRooms: Map<number, number>,
  roomTypes: RoomType[],
  pricingType: 'PerPerson' | 'Fixed' = 'PerPerson'
): number {
  let total = 0;
  selectedRooms.forEach((qty, roomId) => {
    const room = roomTypes.find(r => r.id === roomId);
    if (room) {
      const beds = room.bedsPerUnit || 1;
      // For PerPerson: roomPrice = qty * beds * pricePerPerson
      // For Fixed: roomPrice = qty * price
      if (pricingType === 'PerPerson') {
        total += qty * beds * room.grossPrice;
      } else {
        total += qty * room.grossPrice;
      }
    }
  });
  return total;
}

/**
 * Get bed status for validation
 */
export type BedStatus = 'ok' | 'error' | 'warning';

export function getBedStatus(
  totalBeds: number,
  participantCount: number
): BedStatus {
  if (totalBeds === participantCount) return 'ok';
  if (totalBeds < participantCount) return 'error';
  return 'warning'; // more beds than participants
}

/**
 * Check if booking can proceed
 */
export function canProceedWithBooking(
  totalBeds: number,
  participantCount: number,
  isAdmin: boolean
): boolean {
  // Admin can always proceed (even with insufficient beds)
  if (isAdmin) return true;
  // Customer needs at least enough beds
  return totalBeds >= participantCount;
}
